import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  fetchFplSyncStatus, pingFplApi, fetchAdminAttention, runFplPollNow, runTestEmail,
  FplSyncState, FplPingResult, FplPollResult, TestEmailResult, AttentionItem,
} from "../../api/admin";
import {
  card, secondaryBtn, smallBtn,
  tableStyle, thStyle, tdStyle, badge, errorStyle, panelHeading, pageTitle,
} from "../../styles";

interface Props {
  user: User;
}

function severityStyle(severity: AttentionItem["severity"]) {
  if (severity === "error") return { background: "var(--color-status-bust-bg)", color: "var(--color-status-bust-text)" };
  if (severity === "warning") return { background: "var(--color-surface-alt)", color: "var(--color-warning)" };
  return { background: "var(--color-surface-alt)", color: "var(--color-text-muted)" };
}

export default function OpsHomePage({ user }: Props) {
  const { t } = useTranslation();

  const [attention, setAttention] = useState<AttentionItem[]>([]);
  const [attentionLoading, setAttentionLoading] = useState(true);
  const [attentionError, setAttentionError] = useState("");

  const [syncStatus, setSyncStatus] = useState<FplSyncState[]>([]);
  const [syncStatusTotal, setSyncStatusTotal] = useState(0);
  const [syncStatusPage, setSyncStatusPage] = useState(1);
  const [syncStatusLoading, setSyncStatusLoading] = useState(false);
  const [automationEnabledEnv, setAutomationEnabledEnv] = useState(true);
  const [unconfirmedReadyProposals, setUnconfirmedReadyProposals] = useState(0);
  const [syncStatusError, setSyncStatusError] = useState("");

  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState<FplPingResult | null>(null);

  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState<FplPollResult | null>(null);

  const [testingEmail, setTestingEmail] = useState<"alert" | "success" | null>(null);
  const [testEmailResult, setTestEmailResult] = useState<TestEmailResult | null>(null);

  const loadAttention = () => {
    setAttentionLoading(true);
    setAttentionError("");
    return fetchAdminAttention(user)
      .then((data) => setAttention(data.items))
      .catch(() => setAttentionError(t("admin.opsHome.attention.errors.failedToLoad")))
      .finally(() => setAttentionLoading(false));
  };

  const loadSyncStatus = () => {
    setSyncStatusLoading(true);
    setSyncStatusError("");
    return fetchFplSyncStatus(user, syncStatusPage)
      .then((data) => {
        setSyncStatus(data.items);
        setSyncStatusTotal(data.total);
        setAutomationEnabledEnv(data.automationEnabled);
        setUnconfirmedReadyProposals(data.unconfirmedReadyProposals);
      })
      .catch(() => { setSyncStatus([]); setSyncStatusTotal(0); setSyncStatusError(t("admin.fplAutomation.errors.failedToLoad")); })
      .finally(() => setSyncStatusLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAttention(); }, [user, t]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSyncStatus(); }, [user, syncStatusPage, t]);

  const handlePing = async () => {
    setPinging(true);
    setPingResult(null);
    try {
      setPingResult(await pingFplApi(user));
    } catch {
      setPingResult({ ok: false, error: t("admin.fplAutomation.ping.error") });
    } finally {
      setPinging(false);
    }
  };

  // Pulls the hourly cron's cycle forward on demand. Refreshes both panels afterward so a
  // gameweek that just auto-completed (or started holding) shows up immediately.
  const handlePoll = async () => {
    setPolling(true);
    setPollResult(null);
    try {
      const result = await runFplPollNow(user);
      setPollResult(result);
      await Promise.all([loadAttention(), loadSyncStatus()]);
    } catch (err) {
      setPollResult({ ok: false, lastFetchedAt: null, lastError: null, error: err instanceof Error ? err.message : t("admin.fplAutomation.manualPoll.failed") });
    } finally {
      setPolling(false);
    }
  };

  const handleTestEmail = async (kind: "alert" | "success") => {
    setTestingEmail(kind);
    setTestEmailResult(null);
    try {
      setTestEmailResult(await runTestEmail(user, kind));
    } catch (err) {
      setTestEmailResult({ ok: false, error: err instanceof Error ? err.message : t("admin.opsHome.testEmail.failed") });
    } finally {
      setTestingEmail(null);
    }
  };

  const pollRow = syncStatus.find((s) => s.key === "poll");
  const pollStale = !!pollRow && Date.now() - new Date(pollRow.lastFetchedAt).getTime() > 2 * 60 * 60 * 1000;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <h1 style={pageTitle}>{t("admin.opsHome.title")}</h1>

      <div style={card}>
        <h2 style={panelHeading}>{t("admin.opsHome.attention.title")}</h2>
        {attentionError && <p style={errorStyle}>{attentionError}</p>}
        {attentionLoading ? (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>{t("admin.loading")}</p>
        ) : attention.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>{t("admin.opsHome.attention.empty")}</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {attention.map((item, i) => (
              <Link
                key={`${item.kind}-${i}`}
                to={item.href}
                style={{ ...badge, ...severityStyle(item.severity), display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.6rem 0.85rem", textDecoration: "none", fontWeight: 400 }}
              >
                <span style={{ fontSize: "0.9rem" }}>{item.message}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div style={card}>
        <h2 style={panelHeading}>{t("admin.fplAutomation.ping.title")}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <button style={secondaryBtn} onClick={handlePing} disabled={pinging}>
            {pinging ? t("admin.fplAutomation.ping.testing") : t("admin.fplAutomation.ping.btn")}
          </button>
          {pingResult && (
            pingResult.ok ? (
              <span style={{ fontSize: "0.88rem", color: "var(--color-status-active-text)" }}>
                {t("admin.fplAutomation.ping.success", {
                  players: pingResult.playerCount, teams: pingResult.teamCount,
                  gameweeks: pingResult.gameweekCount, currentGw: pingResult.currentGameweek,
                })}
              </span>
            ) : (
              <span style={{ fontSize: "0.88rem", color: "var(--color-status-bust-text)" }}>
                {t("admin.fplAutomation.ping.failed", { error: pingResult.error })}
              </span>
            )
          )}
          <button style={secondaryBtn} onClick={handlePoll} disabled={polling}>
            {polling ? t("admin.fplAutomation.manualPoll.running") : t("admin.fplAutomation.manualPoll.btn")}
          </button>
          {pollResult && (
            pollResult.error || pollResult.ok === false ? (
              <span style={{ fontSize: "0.88rem", color: "var(--color-status-bust-text)" }}>
                {t("admin.fplAutomation.manualPoll.failed", { error: pollResult.error ?? "" })}
              </span>
            ) : pollResult.lastError ? (
              <span style={{ fontSize: "0.88rem", color: "var(--color-text-muted)" }}>
                {t("admin.fplAutomation.manualPoll.idle", { reason: pollResult.lastError })}
              </span>
            ) : (
              <span style={{ fontSize: "0.88rem", color: "var(--color-status-active-text)" }}>
                {t("admin.fplAutomation.manualPoll.success")}
              </span>
            )
          )}
        </div>
      </div>

      <div style={card}>
        <h2 style={panelHeading}>{t("admin.opsHome.testEmail.title")}</h2>
        <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: "0 0 0.75rem" }}>
          {t("admin.opsHome.testEmail.description")}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <button style={secondaryBtn} onClick={() => handleTestEmail("alert")} disabled={testingEmail !== null}>
            {testingEmail === "alert" ? t("admin.opsHome.testEmail.running") : t("admin.opsHome.testEmail.alertBtn")}
          </button>
          <button style={secondaryBtn} onClick={() => handleTestEmail("success")} disabled={testingEmail !== null}>
            {testingEmail === "success" ? t("admin.opsHome.testEmail.running") : t("admin.opsHome.testEmail.successBtn")}
          </button>
          {testEmailResult && (
            testEmailResult.ok ? (
              <span style={{ fontSize: "0.88rem", color: "var(--color-status-active-text)" }}>{testEmailResult.message}</span>
            ) : (
              <span style={{ fontSize: "0.88rem", color: "var(--color-status-bust-text)" }}>
                {t("admin.opsHome.testEmail.failed", { error: testEmailResult.error ?? "" })}
              </span>
            )
          )}
        </div>
      </div>

      <div style={card}>
        <h2 style={panelHeading}>{t("admin.fplAutomation.syncStatusTitle")}</h2>
        {syncStatusError && <p style={errorStyle}>{syncStatusError}</p>}
        {!automationEnabledEnv && (
          <p style={{ ...errorStyle, padding: "0.6rem 0.75rem", borderRadius: "8px" }}>
            {t("admin.fplAutomation.banners.automationDisabled")}
          </p>
        )}
        {automationEnabledEnv && pollStale && (
          <p style={{ ...errorStyle, padding: "0.6rem 0.75rem", borderRadius: "8px" }}>
            {t("admin.fplAutomation.banners.pollStale")}
          </p>
        )}
        {unconfirmedReadyProposals > 0 && (
          <p style={{
            fontSize: "0.88rem", color: "var(--color-status-active-text)",
            background: "var(--color-status-active-bg)", padding: "0.6rem 0.75rem", borderRadius: "8px",
          }}>
            {t(
              `admin.fplAutomation.banners.unconfirmedProposals_${unconfirmedReadyProposals === 1 ? "one" : "other"}`,
              { count: unconfirmedReadyProposals },
            )}
          </p>
        )}
        {syncStatusLoading ? (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>{t("admin.loading")}</p>
        ) : syncStatus.length === 0 ? (
          <p style={{ color: "var(--color-text-muted)", margin: 0 }}>{t("admin.fplAutomation.syncStatus.empty")}</p>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t("admin.fplAutomation.syncStatus.key")}</th>
                    <th style={thStyle}>{t("admin.fplAutomation.syncStatus.lastAttempted")}</th>
                    <th style={thStyle}>{t("admin.fplAutomation.syncStatus.lastSuccessful")}</th>
                    <th style={thStyle}>{t("admin.fplAutomation.syncStatus.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {syncStatus.map((s) => (
                    <tr key={s.id}>
                      <td style={tdStyle}>{s.key}</td>
                      <td style={tdStyle}>{new Date(s.lastFetchedAt).toLocaleString()}</td>
                      <td style={tdStyle}>{s.lastSuccessAt ? new Date(s.lastSuccessAt).toLocaleString() : t("admin.fplAutomation.syncStatus.never")}</td>
                      <td style={tdStyle}>
                        {s.consecutiveFailures === 0 ? (
                          <span style={{ ...badge, background: "var(--color-status-active-bg)", color: "var(--color-status-active-text)" }}>
                            {t("admin.fplAutomation.syncStatus.ok")}
                          </span>
                        ) : (
                          <span style={{ ...badge, background: "var(--color-status-bust-bg)", color: "var(--color-status-bust-text)" }}>
                            {t("admin.fplAutomation.syncStatus.failing", { count: s.consecutiveFailures, error: s.lastError ?? "" })}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {Math.ceil(syncStatusTotal / 20) > 1 && (
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", padding: "0.75rem 0 0" }}>
                <button style={smallBtn} disabled={syncStatusPage <= 1} onClick={() => setSyncStatusPage((p) => p - 1)}>Previous</button>
                <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>Page {syncStatusPage} of {Math.ceil(syncStatusTotal / 20)}</span>
                <button style={smallBtn} disabled={syncStatusPage >= Math.ceil(syncStatusTotal / 20)} onClick={() => setSyncStatusPage((p) => p + 1)}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
